import React, { useState, useEffect, useRef } from 'react';
import { Container } from "react-bootstrap";
import Particle from '../components/Particle';
import Chatbot from '../components/Chatbot';
import PageTransition from '../components/PageTransition';
import { motion } from "framer-motion";

const Shell = () => {
  const [history, setHistory] = useState([
    'Welcome to Bunty\'s Portfolio Shell!',
    'Type "help" to see available commands.',
    ''
  ]);
  const [currentCommand, setCurrentCommand] = useState('');
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleCommand = (command) => {
    const trimmedCommand = command.trim();
    if (trimmedCommand === '') return;

    setCommandHistory(prev => [...prev, trimmedCommand]);
    setHistoryIndex(-1);

    let output = '';

    const cmd = trimmedCommand.toLowerCase();

    if (cmd === 'help') {
      output = `Available commands:
  help     - Show this help message
  whoami   - Display current user
  ls       - List files in current directory
  pwd      - Print working directory
  echo     - Echo text back
  date     - Show current date and time
  uname    - Show system information
  clear    - Clear the terminal
  history  - Show command history
  cat      - Display file contents
  mkdir    - Create directory
  cd       - Change directory
  ps       - Show running processes
  df       - Show disk usage
  uptime   - Show system uptime
  who      - Show logged in users
  cal      - Show calendar
  fortune  - Display random fortune
  cowsay   - ASCII art cow message
  exit     - Close the terminal (navigates away)`;
    } else if (cmd === 'whoami') {
      output = 'bunty';
    } else if (cmd === 'ls') {
      output = `Documents/  Downloads/  Pictures/  Projects/  resume.pdf`;
    } else if (cmd === 'pwd') {
      output = '/home/portfolio';
    } else if (cmd === 'date') {
      output = new Date().toString();
    } else if (cmd === 'uname') {
      output = 'Portfolio Terminal 1.0.0 #1 SMP Web Browser';
    } else if (cmd === 'clear') {
      setHistory([]);
      return;
    } else if (cmd === 'history') {
      output = commandHistory.map((cmd, idx) => `${idx + 1}  ${cmd}`).join('\n');
    } else if (cmd === 'ps') {
      output = `  PID TTY          TIME CMD
 1234 pts/0    00:00:01 bash
 2345 pts/0    00:00:00 node
 3456 pts/0    00:00:02 react-scripts
 4567 pts/0    00:00:00 portfolio-terminal`;
    } else if (cmd === 'df') {
      output = `Filesystem     1K-blocks    Used Available Use% Mounted on
/dev/sda1       20480000 8123456  12345544  40% /
tmpfs             512000     128    511872   1% /tmp`;
    } else if (cmd === 'uptime') {
      output = ` 09:00:01 up 2 days, 14:32,  1 user,  load average: 0.08, 0.03, 0.01`;
    } else if (cmd === 'who') {
      output = `bunty    pts/0        Dec 12 09:00 (:0.0)`;
    } else if (cmd === 'cal') {
      const now = new Date();
      const month = now.getMonth();
      const year = now.getFullYear();
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
      output = `   ${monthNames[month]} ${year}
Su Mo Tu We Th Fr Sa
       1  2  3  4  5
 6  7  8  9 10 11 12
13 14 15 16 17 18 19
20 21 22 23 24 25 26
27 28 29 30 31`;
    } else if (cmd === 'fortune') {
      const fortunes = [
        "A journey of a thousand miles begins with a single step.",
        "The best way to predict the future is to create it.",
        "Code is poetry in motion.",
        "Debugging is twice as hard as writing code in the first place.",
        "First, solve the problem. Then, write the code.",
        "The only way to do great work is to love what you do."
      ];
      output = fortunes[Math.floor(Math.random() * fortunes.length)];
    } else if (cmd === 'exit') {
      output = 'Goodbye! Redirecting to home...';
      setTimeout(() => window.location.href = '/', 1000);
    } else if (trimmedCommand.startsWith('echo ')) {
      output = trimmedCommand.substring(5);
    } else if (trimmedCommand.startsWith('cat ')) {
      const file = trimmedCommand.substring(4).trim();
      if (file === 'readme.txt') {
        output = 'Welcome to Bunty\'s Portfolio Terminal!\n\nThis is a fun interactive terminal you can use to explore.\nTry typing "help" to see all available commands.';
      } else if (file === 'about.txt') {
        output = 'Hi! I\'m Bunty, a passionate developer who loves creating amazing web experiences.\nCheck out my projects and skills on this portfolio!';
      } else {
        output = `cat: ${file}: No such file or directory`;
      }
    } else if (trimmedCommand.startsWith('mkdir ')) {
      output = `mkdir: created directory '${trimmedCommand.substring(6).trim()}'`;
    } else if (trimmedCommand.startsWith('cd ')) {
      const dir = trimmedCommand.substring(3).trim();
      if (dir === '..') {
        output = 'Changed to parent directory';
      } else {
        output = `cd: ${dir}: No such file or directory`;
      }
    } else if (trimmedCommand.startsWith('cowsay ')) {
      const message = trimmedCommand.substring(7);
      const len = message.length;
      const border = '-'.repeat(len + 2);
      output = ` ${border}
< ${message} >
 ${border}
        \\   ^__^
         \\  (oo)\\_______
            (__)\\       )\\/\\
                ||----w |
                ||     ||`;
    } else if (cmd === 'cowsay') {
      output = ` _______
< Moooo! >
 -------
        \\   ^__^
         \\  (oo)\\_______
            (__)\\       )\\/\\
                ||----w |
                ||     ||`;
    } else {
      output = `Command not found: ${trimmedCommand}. Type "help" for available commands.`;
    }

    setHistory(prev => [...prev, `user@portfolio:~$ ${trimmedCommand}`, output, '']);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleCommand(currentCommand);
    setCurrentCommand('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandHistory.length > 0) {
        const newIndex = historyIndex === -1 ? commandHistory.length - 1 : Math.max(0, historyIndex - 1);
        setHistoryIndex(newIndex);
        setCurrentCommand(commandHistory[newIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex >= 0) {
        const newIndex = historyIndex + 1;
        if (newIndex >= commandHistory.length) {
          setHistoryIndex(-1);
          setCurrentCommand('');
        } else {
          setHistoryIndex(newIndex);
          setCurrentCommand(commandHistory[newIndex]);
        }
      }
    }
  };

  const handleTerminalClick = () => {
    inputRef.current?.focus();
  };

  return (
    <PageTransition>
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Container style={{padding: '80px 20px 60px 20px', flex: 1}}>
          <Particle />
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: "spring", stiffness: 100, damping: 15 }}
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.85)',
              color: 'white',
              fontFamily: 'monospace',
              padding: '25px',
              borderRadius: '16px',
              minHeight: '450px',
              maxWidth: '800px',
              margin: '20px auto 50px auto',
              boxShadow: '0 12px 40px rgba(124, 58, 237, 0.25)',
              border: '1px solid var(--glass-border)',
              cursor: 'text',
              position: 'relative',
              zIndex: 1000,
              backdropFilter: 'blur(10px)'
            }}
            onClick={handleTerminalClick}
          >
        <div style={{ marginBottom: '10px', fontSize: '14px', lineHeight: '1.4' }}>
          {history.map((line, index) => (
            <div key={index} style={{ whiteSpace: 'pre-wrap' }}>
              {line}
            </div>
          ))}
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: '8px', color: 'white' }}>user@portfolio:~$</span>
          <input
            ref={inputRef}
            type="text"
            value={currentCommand}
            onChange={(e) => setCurrentCommand(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: 'white',
              fontFamily: 'monospace',
              fontSize: '14px',
              flex: 1,
              outline: 'none',
              caretColor: 'white'
            }}
            autoComplete="off"
          />
        </form>
      </motion.div>
      <Chatbot />
    </Container>
    </div>
    </PageTransition>
  );
};

export default Shell;
